import { gql } from '@apollo/client';
import { RESOURCE_INFO } from '../../../pages/DomainDetails/hooks/useResourceInfoQuery';
import { getRegion } from '../../../utils/helpers';
import { QueryConfig } from './types';

/**
 * Converts domainIdentifier (clientId:domainName) to the identuple format
 * required by metrics APIs: swift-{region}-{stage}/{accountId}/{domainName}/1
 */
function buildDomainIdentuple(domainIdentifier: string): string {
  const host = window.location.host;
  const parts = host.split('.');
  const airportCode = parts[0] || 'dub';
  const currentStage = parts[1] || 'beta';

  const stageMapping: Record<string, string> = {
    beta: 'staging',
    gamma: 'integ',
    prod: 'prod',
  };

  const regionInfo = getRegion(airportCode);
  const fullRegion = regionInfo.publicRegion;
  const stageName = stageMapping[currentStage] || 'staging';
  const [accountId, domainName] = domainIdentifier.split(':');

  return `swift-${fullRegion}-${stageName}/${accountId}/${domainName}/1`;
}

// Stub queries for query IDs whose exact import paths are not yet known.
// These match the expected variable shapes from the design document.

const CLUSTER_INFO = gql`
  query getClusterInfo($domainIdentifier: String!) {
    domain(domainIdentifier: $domainIdentifier) {
      es60 {
        cluster {
          health
          stats
          settings
          state
        }
      }
      kibana {
        health
      }
      clusterData {
        serverType
        discoveryMode
      }
    }
  }
`;

const GET_CLUSTER_HEALTH_SUMMARY = gql`
  query getClusterHealthSummary($domainIdentifier: String!, $domainIdentuple: String!) {
    clusterHealth(domainIdentifier: $domainIdentifier, domainIdentuple: $domainIdentuple)
    nodes(domainIdentifier: $domainIdentifier) {
      numberOfNodes
    }
    clusterStats(domainIdentifier: $domainIdentifier)
    workloadClassification(domainIdentifier: $domainIdentifier)
  }
`;

const GET_INSIGHTS = gql`
  query GetInsights(
    $entityType: String
    $entityValue: String
    $startTime: Float
    $endTime: Float
    $filters: String
  ) {
    getInsights(
      entityType: $entityType
      entityValue: $entityValue
      startTime: $startTime
      endTime: $endTime
      filters: $filters
    )
  }
`;

const GET_INSIGHT_DETAILS = gql`
  query getInsightDetails(
    $entityType: String
    $entityValue: String
    $insightID: String
  ) {
    getInsightDetails(
      entityType: $entityType
      entityValue: $entityValue
      insightID: $insightID
    )
  }
`;

export const QUERY_REGISTRY: Record<string, QueryConfig> = {
  RESOURCE_INFO: {
    gqlQuery: RESOURCE_INFO,
    buildVariables: (domainIdentifier: string) => ({ domainIdentifier }),
  },
  CLUSTER_INFO: {
    gqlQuery: CLUSTER_INFO,
    buildVariables: (domainIdentifier: string) => ({
      domainIdentifier,
    }),
  },
  CLUSTER_HEALTH_SUMMARY: {
    gqlQuery: GET_CLUSTER_HEALTH_SUMMARY,
    buildVariables: (domainIdentifier: string) => ({
      domainIdentifier,
      domainIdentuple: buildDomainIdentuple(domainIdentifier),
    }),
  },
  GET_INSIGHTS: {
    gqlQuery: GET_INSIGHTS,
    buildVariables: (domainIdentifier: string) => ({
      entityType: 'DOMAINIDENT',
      entityValue: buildDomainIdentuple(domainIdentifier),
      startTime: Date.now() - 3600000,
      endTime: Date.now(),
    }),
  },
  GET_INSIGHT_DETAILS: {
    gqlQuery: GET_INSIGHT_DETAILS,
    buildVariables: (domainIdentifier: string) => ({
      entityType: 'DOMAINIDENT',
      entityValue: buildDomainIdentuple(domainIdentifier),
      insightID: '',
    }),
  },
  TMS_ISSUES: {
    gqlQuery: gql`
      query getDomainTMSIssues($domainIdentifier: String!) {
        domain(domainIdentifier: $domainIdentifier) {
          tmsIssues
        }
      }
    `,
    buildVariables: (domainIdentifier: string) => ({ domainIdentifier }),
  },
  DIAGNOSTIC_DUMPS_EXIST: {
    gqlQuery: gql`
      query CheckDiagnosticDumpsExist($domainId: String!, $startTime: String!, $hours: String) {
        checkDiagnosticDumpsExist(domainId: $domainId, startTime: $startTime, hours: $hours)
      }
    `,
    buildVariables: (domainIdentifier: string) => ({
      domainId: domainIdentifier,
      startTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      hours: '24',
    }),
  },
  KEY_EVENTS: {
    gqlQuery: gql`
      query getKeyEvents($domainId: String!, $startTime: String, $endTime: String) {
        domainKeyEvents(domainId: $domainId, startTime: $startTime, endTime: $endTime) {
          keyEvents {
            event_time
            source
            action
            action_state
            host
            domain_ident
            id
            tagging
            version
            dynamicFields
          }
        }
      }
    `,
    buildVariables: (domainIdentifier: string) => ({
      domainId: domainIdentifier,
      startTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      endTime: new Date().toISOString(),
    }),
  },
  CW_METRICS: {
    gqlQuery: gql`
      query getExternalCwMetrics(
        $domainIdentifier: String!
        $metrics: String!
        $startTime: Date!
        $endTime: Date!
        $period: Int!
      ) {
        cwMetrics(
          domainIdentifier: $domainIdentifier
          metrics: $metrics
          startTime: $startTime
          endTime: $endTime
          period: $period
        )
      }
    `,
    buildVariables: (domainIdentifier: string) => ({
      domainIdentifier,
      metrics: '[]',
      startTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      endTime: new Date(),
      period: 900,
    }),
    transformTimeRange: ({ startTime, endTime }) => ({
      startTime: new Date(startTime),
      endTime: new Date(endTime),
    }),
  },
};
